import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Result,
  pipeVelocity, toBase, fromBase, altsFor, fmt, UNITS,
  SpecCard, SpecValue,
} from "./_shared";

export default function PipeVelocityCalculator() {
  const [flowVal, setFlowVal] = useState(2);
  const [flowUnit, setFlowUnit] = useState("Lps");
  const [idVal, setIdVal] = useState(50);
  const [idUnit, setIdUnit] = useState("mm");

  const flow_m3s = toBase("flow", flowVal, flowUnit);
  const id_m = toBase("length_small", idVal, idUnit) / 1000;

  const v_mps = useMemo(() => pipeVelocity({ flow_m3s, id_m }), [flow_m3s, id_m]);

  const status = !v_mps ? undefined
               : v_mps > 3.0 ? "alert"
               : v_mps > 2.5 ? "warn"
               : v_mps < 0.6 ? "warn"
               : "ok";
  const note   = !v_mps ? null
               : v_mps > 3.0 ? "Above 3.0 m/s — erosion, noise and water hammer risk. Up-size the pipe."
               : v_mps > 2.5 ? "Above 2.5 m/s — acceptable for short discharge runs only. Re-check head loss."
               : v_mps < 0.6 ? "Below 0.6 m/s — sediment will settle in horizontal runs."
               : v_mps < 1.0 ? "Within the suction / gravity band (0.6–1.5 m/s)."
               :               "Within the recommended discharge band (1.0–2.5 m/s).";

  return (
    <ToolShell
      slug="pipe-velocity"
      title="Pipe velocity check"
      oneLiner="Quick sanity check on flow velocity through a pipe. Erosive above 3 m/s, sediment-prone below 0.6 m/s."
      onExport={() => ({
        inputs: [
          { label: "Flow rate", value: flowVal, unit: UNITS.flow.labels[flowUnit] },
          { label: "Internal diameter", value: idVal, unit: UNITS.length_small.labels[idUnit] },
        ],
        results: [
          {
            label: "Velocity",
            value: fmt(v_mps), unit: "m/s",
            alts: v_mps !== null ? [{ value: fmt(fromBase("velocity", v_mps, "fps")), unit: "ft/s" }] : null,
            status, note,
          },
          {
            label: "Volumetric flow",
            value: fmt(flow_m3s * 3600), unit: "m³/h",
            alts: altsFor("flow", flow_m3s, ["Lps", "Lpm", "gpm"], 1),
          },
        ],
        notes: ["Formula: V = Q / A,  A = π D² / 4."],
        spec: v_mps !== null ? {
          title: "Velocity check",
          body: `<strong>${flowVal} ${UNITS.flow.labels[flowUnit]}</strong> through a <strong>${idVal} ${UNITS.length_small.labels[idUnit]} ID</strong> pipe gives a velocity of <strong>${fmt(v_mps, 2)} m/s</strong> (volumetric flow <strong>${fmt(flow_m3s * 3600, 2)} m³/h</strong>).`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Flow rate">
          <UnitNumber dim="flow" value={flowVal} onChange={setFlowVal} unit={flowUnit} onUnitChange={setFlowUnit}
            units={["Lps", "Lpm", "m3h", "m3d", "gpm"]} testid="pv-flow" />
        </Field>
        <Field label="Internal diameter">
          <UnitNumber dim="length_small" value={idVal} onChange={setIdVal} unit={idUnit} onUnitChange={setIdUnit}
            units={["mm", "cm", "m", "inch"]} testid="pv-id" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <Result
          label="Velocity"
          primary={{ value: fmt(v_mps), unit: "m/s" }}
          alts={v_mps !== null ? [{ value: fmt(fromBase("velocity", v_mps, "fps")), unit: UNITS.velocity.labels.fps }] : null}
          status={status}
          note={note}
          testid="pv-result"
        />
        <Result
          label="Volumetric flow"
          primary={{ value: fmt(flow_m3s * 3600), unit: "m³/h" }}
          alts={altsFor("flow", flow_m3s, ["Lps", "Lpm", "gpm"], 1)}
          testid="pv-result-flow"
        />
      </div>

      {v_mps !== null && (
        <SpecCard
          eyebrow="Velocity check"
          testid="pv-spec-card"
          footer={note}
        >
          <SpecValue>{flowVal} {UNITS.flow.labels[flowUnit]}</SpecValue> through a{" "}
          <SpecValue>{idVal} {UNITS.length_small.labels[idUnit]} ID</SpecValue> pipe gives a velocity of{" "}
          <SpecValue>{fmt(v_mps, 2)} m/s</SpecValue> (volumetric flow{" "}
          <SpecValue>{fmt(flow_m3s * 3600, 2)} m³/h</SpecValue>).
        </SpecCard>
      )}
    </ToolShell>
  );
}
